import { useState, type FormEvent } from "react";
import { useNavigate } from "react-router";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/shared/components/ui/AlertDialog";
import { Button } from "@/shared/components/ui/Button";
import { PasswordFormField } from "@/shared/components/form/PasswordFormField";
import { useDeleteAccount } from "@/shared/lib/auth";

/**
 * Destructive confirmation for permanently deleting the account. Requires the
 * current password; the user-row delete cascades to all owned data. On success
 * the session is gone, so route back to the login page.
 */
export function DeleteAccountDialog() {
  const navigate = useNavigate();
  const remove = useDeleteAccount();
  const [open, setOpen] = useState(false);
  const [currentPassword, setCurrentPassword] = useState("");

  function handleOpenChange(next: boolean) {
    setOpen(next);
    if (!next) {
      setCurrentPassword("");
      remove.reset();
    }
  }

  function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    remove.mutate(
      { currentPassword },
      { onSuccess: () => navigate("/auth/login") },
    );
  }

  return (
    <AlertDialog open={open} onOpenChange={handleOpenChange}>
      <AlertDialogTrigger asChild>
        <Button variant="destructive">Delete account</Button>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Delete your account?</AlertDialogTitle>
          <AlertDialogDescription>
            This permanently deletes your account and <strong>all</strong> of your
            data — contacts, households, lists, and share links. This cannot be
            undone.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <form className="flex flex-col gap-4" onSubmit={handleSubmit}>
          <PasswordFormField
            label="Current password"
            name="currentPassword"
            autoComplete="current-password"
            required
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e.target.value)}
          />
          {remove.error ? (
            <p className="type-body-sm text-danger-strong mx-4">
              {remove.error.message}
            </p>
          ) : null}
          <AlertDialogFooter>
            <Button
              type="submit"
              variant="destructive"
              disabled={remove.isPending || currentPassword.length === 0}
            >
              {remove.isPending ? "Deleting…" : "Delete account"}
            </Button>
            <Button
              type="button"
              variant="ghost"
              onClick={() => handleOpenChange(false)}
            >
              Cancel
            </Button>
          </AlertDialogFooter>
        </form>
      </AlertDialogContent>
    </AlertDialog>
  );
}
